export class RulesResultsLayout {
	constructor(i18n, containerNode) {
		this.__ = i18n;
		this.containerNode = containerNode;
		this.scopeMode = 'page';
		this.lastRenderedPages = [];
	}

	isReady() {
		return Boolean(this.containerNode);
	}

	bindRuleDetails(onRuleDetails) {
		this.containerNode.addEventListener('click', (event) => {
			const ruleButton = event.target.closest('[data-rule-index][data-rule-key]');

			if (!ruleButton) {
				return;
			}

			const pageIndex = Number.parseInt(ruleButton.dataset.ruleIndex || '', 10);
			const ruleKey = ruleButton.dataset.ruleKey || '';

			if (Number.isNaN(pageIndex) || !ruleKey) {
				return;
			}

			onRuleDetails(pageIndex, ruleKey);
		});
	}

	bindRuleUrlOpen(onRuleUrlOpen) {
		this.containerNode.addEventListener('click', (event) => {
			const triggerNode = event.target.closest('[data-rule-page-index][data-rule-open]');

			if (!triggerNode) {
				return;
			}

			if (triggerNode.tagName === 'A') {
				event.preventDefault();
			}

			const pageIndex = Number.parseInt(triggerNode.dataset.rulePageIndex || '', 10);

			if (Number.isNaN(pageIndex)) {
				return;
			}

			onRuleUrlOpen(pageIndex);
		});
	}

	render(pages) {
		this.lastRenderedPages = Array.isArray(pages) ? pages : [];

		if (pages.length === 0) {
			this.containerNode.classList.remove('has-list-table');
			this.containerNode.textContent = this.__( 'No pages crawled yet.', 'a11yfix' );
			return;
		}

		this.containerNode.classList.add('has-list-table');
		this.containerNode.innerHTML = '';

		const orderedPages = pages
			.map((page, originalIndex) => ({ page, originalIndex }))
			.sort((leftItem, rightItem) => {
				const leftTime = typeof leftItem.page?.crawledAt === 'string'
					? new Date(leftItem.page.crawledAt).getTime()
					: Number.NaN;
				const rightTime = typeof rightItem.page?.crawledAt === 'string'
					? new Date(rightItem.page.crawledAt).getTime()
					: Number.NaN;

				if (Number.isFinite(leftTime) && Number.isFinite(rightTime) && leftTime !== rightTime) {
					return rightTime - leftTime;
				}

				if (Number.isFinite(leftTime) !== Number.isFinite(rightTime)) {
					return Number.isFinite(rightTime) ? 1 : -1;
				}

				return rightItem.originalIndex - leftItem.originalIndex;
			});

		const summary = document.createElement('p');
		summary.textContent = `${this.__( 'Stored pages:', 'a11yfix' )} ${pages.length}`;
		this.containerNode.appendChild(summary);

		const table = document.createElement('table');
		table.className = 'a11yfix-results-table a11yfix-rules-table widefat fixed striped table-view-list';
		const head = document.createElement('thead');
		const headRow = document.createElement('tr');
		const headUrl = document.createElement('th');
		const headPostId = document.createElement('th');
		const headImageRule = document.createElement('th');
		const headLinksRule = document.createElement('th');
		const headHeadingRule = document.createElement('th');
		const headFormLabelsRule = document.createElement('th');
		const headAccessibleNamePreferredSourceRule = document.createElement('th');
		const headAriaLabelledbyRule = document.createElement('th');
		const headAriaHiddenInteractiveRule = document.createElement('th');
		const headKeyboardFocusableRule = document.createElement('th');
		const headLowContrastRule = document.createElement('th');
		const headActions = document.createElement('th');

		headUrl.textContent = this.__( 'URL', 'a11yfix' );
		headPostId.textContent = this.__( 'ID', 'a11yfix' );
		headImageRule.textContent = this.__( 'Images alt rule', 'a11yfix' );
		headLinksRule.textContent = this.__( 'Link text rule', 'a11yfix' );
		headHeadingRule.textContent = this.__( 'Heading order rule', 'a11yfix' );
		headFormLabelsRule.textContent = this.__( 'Form labels rule', 'a11yfix' );
		headAccessibleNamePreferredSourceRule.textContent = this.__( 'Accessible name source rule', 'a11yfix' );
		headAriaLabelledbyRule.textContent = this.__( 'aria-labelledby integrity rule', 'a11yfix' );
		headAriaHiddenInteractiveRule.textContent = this.__( 'aria-hidden interactive rule', 'a11yfix' );
		headKeyboardFocusableRule.textContent = this.__( 'Keyboard focusable rule', 'a11yfix' );
		headLowContrastRule.textContent = this.__( 'Low contrast rule', 'a11yfix' );
		headActions.textContent = this.__( 'Actions', 'a11yfix' );
		headRow.appendChild(headUrl);
		headRow.appendChild(headPostId);
		headRow.appendChild(headImageRule);
		headRow.appendChild(headLinksRule);
		headRow.appendChild(headHeadingRule);
		headRow.appendChild(headFormLabelsRule);
		headRow.appendChild(headAccessibleNamePreferredSourceRule);
		headRow.appendChild(headAriaLabelledbyRule);
		headRow.appendChild(headAriaHiddenInteractiveRule);
		headRow.appendChild(headKeyboardFocusableRule);
		headRow.appendChild(headLowContrastRule);
		headRow.appendChild(headActions);
		head.appendChild(headRow);
		table.appendChild(head);

		const body = document.createElement('tbody');

		for (const { page, originalIndex } of orderedPages) {
			const row = document.createElement('tr');
			const urlCell = document.createElement('td');
			const postIdCell = document.createElement('td');
			const imageRuleCell = document.createElement('td');
			const linksRuleCell = document.createElement('td');
			const headingRuleCell = document.createElement('td');
			const formLabelsRuleCell = document.createElement('td');
			const accessibleNamePreferredSourceRuleCell = document.createElement('td');
			const ariaLabelledbyRuleCell = document.createElement('td');
			const ariaHiddenInteractiveRuleCell = document.createElement('td');
			const keyboardFocusableRuleCell = document.createElement('td');
			const lowContrastRuleCell = document.createElement('td');
			const actionsCell = document.createElement('td');
			const link = document.createElement('a');
			const viewAndFixButton = document.createElement('button');
			const depthLabel = document.createElement('span');

			link.href = page.url;
			link.dataset.rulePageIndex = String(originalIndex);
			link.dataset.ruleOpen = 'url';
			link.textContent = page.url;

			viewAndFixButton.type = 'button';
			viewAndFixButton.className = 'button button-secondary button-small';
			viewAndFixButton.dataset.rulePageIndex = String(originalIndex);
			viewAndFixButton.dataset.ruleOpen = 'view-and-fix';
			viewAndFixButton.textContent = this.__( 'View and Fix', 'a11yfix' );

			depthLabel.textContent = ` (d=${page.depth})`;
			postIdCell.textContent = this.getPageIdDisplayValue(page);
			urlCell.appendChild(link);
			urlCell.appendChild(depthLabel);
			actionsCell.appendChild(viewAndFixButton);
			imageRuleCell.dataset.ruleCellIndex = String(originalIndex);
			imageRuleCell.dataset.ruleCellKey = 'imageAlt';
			linksRuleCell.dataset.ruleCellIndex = String(originalIndex);
			linksRuleCell.dataset.ruleCellKey = 'linkTitle';
			headingRuleCell.dataset.ruleCellIndex = String(originalIndex);
			headingRuleCell.dataset.ruleCellKey = 'headingOrder';
			formLabelsRuleCell.dataset.ruleCellIndex = String(originalIndex);
			formLabelsRuleCell.dataset.ruleCellKey = 'formLabels';
			accessibleNamePreferredSourceRuleCell.dataset.ruleCellIndex = String(originalIndex);
			accessibleNamePreferredSourceRuleCell.dataset.ruleCellKey = 'accessibleNamePreferredSource';
			ariaLabelledbyRuleCell.dataset.ruleCellIndex = String(originalIndex);
			ariaLabelledbyRuleCell.dataset.ruleCellKey = 'ariaLabelledbyIntegrity';
			ariaHiddenInteractiveRuleCell.dataset.ruleCellIndex = String(originalIndex);
			ariaHiddenInteractiveRuleCell.dataset.ruleCellKey = 'ariaHiddenInteractive';
			keyboardFocusableRuleCell.dataset.ruleCellIndex = String(originalIndex);
			keyboardFocusableRuleCell.dataset.ruleCellKey = 'keyboardFocusable';
			lowContrastRuleCell.dataset.ruleCellIndex = String(originalIndex);
			lowContrastRuleCell.dataset.ruleCellKey = 'lowColorContrast';
			this.renderRuleCell(imageRuleCell, page, originalIndex, 'imageAlt');
			this.renderRuleCell(linksRuleCell, page, originalIndex, 'linkTitle');
			this.renderRuleCell(headingRuleCell, page, originalIndex, 'headingOrder');
			this.renderRuleCell(formLabelsRuleCell, page, originalIndex, 'formLabels');
			this.renderRuleCell(accessibleNamePreferredSourceRuleCell, page, originalIndex, 'accessibleNamePreferredSource');
			this.renderRuleCell(ariaLabelledbyRuleCell, page, originalIndex, 'ariaLabelledbyIntegrity');
			this.renderRuleCell(ariaHiddenInteractiveRuleCell, page, originalIndex, 'ariaHiddenInteractive');
			this.renderRuleCell(keyboardFocusableRuleCell, page, originalIndex, 'keyboardFocusable');
			this.renderRuleCell(lowContrastRuleCell, page, originalIndex, 'lowColorContrast');

			row.appendChild(urlCell);
			row.appendChild(postIdCell);
			row.appendChild(imageRuleCell);
			row.appendChild(linksRuleCell);
			row.appendChild(headingRuleCell);
			row.appendChild(formLabelsRuleCell);
			row.appendChild(accessibleNamePreferredSourceRuleCell);
			row.appendChild(ariaLabelledbyRuleCell);
			row.appendChild(ariaHiddenInteractiveRuleCell);
			row.appendChild(keyboardFocusableRuleCell);
			row.appendChild(lowContrastRuleCell);
			row.appendChild(actionsCell);
			body.appendChild(row);
		}

		table.appendChild(body);
		this.containerNode.appendChild(table);
	}

	getScopeMode() {
		return this.scopeMode === 'all' ? 'all' : 'page';
	}

	setScopeMode(mode) {
		const normalizedMode = mode === 'all' ? 'all' : 'page';

		if (this.scopeMode === normalizedMode) {
			return;
		}

		this.scopeMode = normalizedMode;
		this.render(this.lastRenderedPages);
	}

	toggleScopeMode() {
		this.scopeMode = this.scopeMode === 'page' ? 'all' : 'page';
		this.render(this.lastRenderedPages);
	}

	updateRuleStatus(pageIndex, page) {
		const imageRuleCell = this.containerNode.querySelector(`[data-rule-cell-index="${pageIndex}"][data-rule-cell-key="imageAlt"]`);
		const linksRuleCell = this.containerNode.querySelector(`[data-rule-cell-index="${pageIndex}"][data-rule-cell-key="linkTitle"]`);
		const headingRuleCell = this.containerNode.querySelector(`[data-rule-cell-index="${pageIndex}"][data-rule-cell-key="headingOrder"]`);
		const formLabelsRuleCell = this.containerNode.querySelector(`[data-rule-cell-index="${pageIndex}"][data-rule-cell-key="formLabels"]`);
		const accessibleNamePreferredSourceRuleCell = this.containerNode.querySelector(`[data-rule-cell-index="${pageIndex}"][data-rule-cell-key="accessibleNamePreferredSource"]`);
		const ariaLabelledbyRuleCell = this.containerNode.querySelector(`[data-rule-cell-index="${pageIndex}"][data-rule-cell-key="ariaLabelledbyIntegrity"]`);
		const ariaHiddenInteractiveRuleCell = this.containerNode.querySelector(`[data-rule-cell-index="${pageIndex}"][data-rule-cell-key="ariaHiddenInteractive"]`);
		const keyboardFocusableRuleCell = this.containerNode.querySelector(`[data-rule-cell-index="${pageIndex}"][data-rule-cell-key="keyboardFocusable"]`);
		const lowContrastRuleCell = this.containerNode.querySelector(`[data-rule-cell-index="${pageIndex}"][data-rule-cell-key="lowColorContrast"]`);

		if (!imageRuleCell || !linksRuleCell || !headingRuleCell || !formLabelsRuleCell || !accessibleNamePreferredSourceRuleCell || !ariaLabelledbyRuleCell || !ariaHiddenInteractiveRuleCell || !keyboardFocusableRuleCell || !lowContrastRuleCell) {
			return;
		}

		this.renderRuleCell(imageRuleCell, page, pageIndex, 'imageAlt');
		this.renderRuleCell(linksRuleCell, page, pageIndex, 'linkTitle');
		this.renderRuleCell(headingRuleCell, page, pageIndex, 'headingOrder');
		this.renderRuleCell(formLabelsRuleCell, page, pageIndex, 'formLabels');
		this.renderRuleCell(accessibleNamePreferredSourceRuleCell, page, pageIndex, 'accessibleNamePreferredSource');
		this.renderRuleCell(ariaLabelledbyRuleCell, page, pageIndex, 'ariaLabelledbyIntegrity');
		this.renderRuleCell(ariaHiddenInteractiveRuleCell, page, pageIndex, 'ariaHiddenInteractive');
		this.renderRuleCell(keyboardFocusableRuleCell, page, pageIndex, 'keyboardFocusable');
		this.renderRuleCell(lowContrastRuleCell, page, pageIndex, 'lowColorContrast');
	}

	renderRuleCell(ruleCell, page, pageIndex, ruleKey) {
		ruleCell.innerHTML = '';
		const status = this.getEffectiveRuleStatus(page, ruleKey);

		if (ruleKey === 'imageAlt' && this.hasNoImages(page) && status !== 'fail') {
			const mark = document.createElement('span');
			mark.className = 'a11yfix-rule-status-badge a11yfix-rule-status-badge--none';
			mark.textContent = this.__( 'No images', 'a11yfix' );
			ruleCell.appendChild(mark);
			return;
		}

		const ruleButton = document.createElement('button');
		ruleButton.type = 'button';
		ruleButton.className = 'button button-link a11yfix-rule-button';
		ruleButton.classList.add(this.getRuleStatusClass(status));
		ruleButton.textContent = this.getRuleStatusSign(status);

		if (this.canOpenRuleDetails(page, ruleKey, status)) {
			ruleButton.dataset.ruleIndex = String(pageIndex);
			ruleButton.dataset.ruleKey = ruleKey;
		} else {
			ruleButton.disabled = true;
			ruleButton.classList.add('a11yfix-rule-button-disabled');
		}

		const ruleParts = page?.rulePartsByRule?.[ruleKey];
		const hasRuleParts = this.scopeMode === 'all'
			&& status === 'fail'
			&& Array.isArray(ruleParts?.labels)
			&& ruleParts.labels.length > 0;

		if (hasRuleParts) {
			const marks = document.createElement('span');
			marks.className = 'a11yfix-rule-part-marks';
			const localizedPartLabels = ruleParts.labels.map((label) => this.translatePartLabel(label));
			marks.textContent = ` (${localizedPartLabels.join(', ')})`;
			ruleCell.appendChild(ruleButton);
			ruleCell.appendChild(marks);
			return;
		}

		ruleCell.appendChild(ruleButton);
	}

	hasNoImages(page) {
		const images = page?.ruleDetails?.imageAlt?.images;
		return Array.isArray(images) && images.length === 0;
	}

	canOpenRuleDetails(page, ruleKey, status) {
		if (status === 'skipped') {
			return false;
		}

		if (ruleKey === 'imageAlt' && this.hasNoImages(page)) {
			return false;
		}

		return true;
	}

	getRuleStatusSign(status) {
		if (status === 'pass') {
			return this.__( 'Pass', 'a11yfix' );
		}

		if (status === 'fail') {
			return this.__( 'Fail', 'a11yfix' );
		}

		if (status === 'skipped') {
			return this.__( 'Skipped', 'a11yfix' );
		}

		return this.__( 'Not checked', 'a11yfix' );
	}

	getRuleStatusClass(status) {
		if (status === 'pass') {
			return 'a11yfix-rule-button--pass';
		}

		if (status === 'fail') {
			return 'a11yfix-rule-button--fail';
		}

		if (status === 'skipped') {
			return 'a11yfix-rule-button--skipped';
		}

		return 'a11yfix-rule-button--pending';
	}

	getEffectiveRuleStatus(page, ruleKey) {
		const rawStatus = String(page?.rules?.[ruleKey] || 'not_checked');

		if (this.scopeMode !== 'page' || rawStatus !== 'fail') {
			return rawStatus;
		}

		const labels = Array.isArray(page?.rulePartsByRule?.[ruleKey]?.labels)
			? page.rulePartsByRule[ruleKey].labels.map((label) => String(label || '').toLowerCase())
			: [];

		if (labels.length === 0) {
			return rawStatus;
		}

		return labels.includes('page') ? 'fail' : 'pass';
	}

	getPageIdDisplayValue(page) {
		const pageType = String(page?.pageType || '').toLowerCase();

		if (pageType === 'home' || pageType === 'archive') {
			return this.__( pageType, 'a11yfix' );
		}

		return page?.postId ? String(page.postId) : this.__( 'N/A', 'a11yfix' );
	}

	translatePartLabel(label) {
		const normalized = String(label || '').trim().toLowerCase();

		if (normalized === 'header') {
			return this.__( 'header', 'a11yfix' );
		}

		if (normalized === 'footer') {
			return this.__( 'footer', 'a11yfix' );
		}

		if (normalized === 'page') {
			return this.__( 'page', 'a11yfix' );
		}

		return String(label || '');
	}
}
